import { useAsync, useLogger } from "@kosmod/kit";
import * as three from 'three';

import { useMemo, useRef, useState } from "@lukekaalim/act";
import { assetURLs, useDisposable } from "@kosmod/graphics-3d";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import { assetsURLs } from "../assets";
import { BUILTIN_THEATRE_ASSETS } from "../defs";

export type Loadable = [name: string, state: boolean]

/**
 * All of the basic resources (texture/models/sfx)
 * that the Theatre needs to start up
 */
export type TheatreCoreResources = {
  gridTileMap: three.Texture,
  starHemisphere: three.Mesh,
  starDiamond: three.Texture,

  cubeLo: three.Mesh,

  builtin2D: Record<string, { texture: three.Texture, url: URL }>,
  builtin3D: Record<string, three.Object3D>,
}


export const useTheatreCoreResource = (): [null | TheatreCoreResources, Loadable[]] => {
  const [textureLoader] = useState(() => new three.TextureLoader());
  const [gltfLoader] = useState(() => new GLTFLoader());

  const logger = useLogger('TheatreCoreResources');

  const { result: gridTileMap } = useAsync(async () => {
    const tileMap = await textureLoader.loadAsync(assetsURLs.gridTileMap);
    tileMap.magFilter = three.NearestFilter
    tileMap.minFilter = three.NearestFilter
    logger.log('GridTileMap Loaded')
    return tileMap
  }, [])

  const { result: starHemisphere } = useAsync(async () => {
    const gltf = await gltfLoader.loadAsync(assetsURLs.starHemisphere);
    logger.log('StarHemisphere Loaded')
    return gltf.scene.children.find(c => c.name === 'StarHemisphere') as three.Mesh;
  }, [])
  const { result: starDiamond, state: STAR_DIAMOND_STATUS } = useAsync(async () => {
    return await textureLoader.loadAsync(assetURLs.starryDiamond);
  }, [])
  const { result: cubeLo } = useAsync(async () => {
    const gltf = await gltfLoader.loadAsync(assetsURLs.dice);
    logger.log('CubeLo Loaded')
    const cubeLo = gltf.scene.children.find(c => c.name === 'd8output') as three.Mesh;
    logger.log(cubeLo)
    return cubeLo;
  }, [])

  const { result: DICE_SET, state: DICE_SET_STATUS } = useAsync(async () => {
    const gltf = await gltfLoader.loadAsync(assetsURLs.dice);
    return gltf.scene;
  }, [])
  const { result: TERRAIN_PARTS, state: TERRAIN_PARTS_STATUS } = useAsync(async () => {
    const gltf = await gltfLoader.loadAsync(assetsURLs.terrainSystems);
    return gltf.scene;
  }, [])
  const { result: PALADIN, state: TERRAIN_PARTS_PALADIN } = useAsync(async () => {
    const tex = await textureLoader.loadAsync(assetsURLs.paladin);
    tex.magFilter = three.NearestFilter
    tex.minFilter = three.NearestFilter
    tex.colorSpace = three.SRGBColorSpace;
    return tex;
  }, [])

  const loadables: Loadable[] = [
    ['Terrain.GLB', TERRAIN_PARTS_STATUS === 'resolved'],
    ['Dice.GLB', DICE_SET_STATUS === 'resolved'],
    ['Particles.texture', STAR_DIAMOND_STATUS === 'resolved']
  ]

  const resources = useMemo(() => {
    if (!gridTileMap || !starHemisphere || !starDiamond || !cubeLo || !DICE_SET || !TERRAIN_PARTS || !PALADIN)
      return null;

    return {
      gridTileMap,
      starHemisphere,
      starDiamond,
      cubeLo,
      builtin3D: {
        [BUILTIN_THEATRE_ASSETS.DICE_SET]: DICE_SET,
        [BUILTIN_THEATRE_ASSETS.DOIS_TERRAIN]: TERRAIN_PARTS,
      },
      builtin2D: {
        [BUILTIN_THEATRE_ASSETS.PALADIN]: { texture: PALADIN, url: new URL(assetsURLs.paladin) },
      }
    }
  }, [gridTileMap, starHemisphere, starDiamond, cubeLo, DICE_SET, TERRAIN_PARTS])

  return [resources, loadables];
};
